import { motion } from "motion/react"
import { cx } from "@/lib/utils/cx"

// Step rail used at the top of every estimate wizard card.
//
// Generic over `labels` so QE3's 4 steps and full-estimate's 6 steps
// share the same visuals. The current segment animates a pulsing dot;
// completed segments fill solid emerald.

interface ProgressRailProps {
  step: number
  labels: readonly string[]
}

export function ProgressRail({ step, labels }: ProgressRailProps) {
  return (
    <div className="px-5 md:px-10 pt-5 md:pt-9 pb-1">
      <div className="flex items-center gap-1.5">
        {labels.map((_label, idx) => {
          const phase: "done" | "current" | "upcoming" =
            idx < step ? "done" : idx === step ? "current" : "upcoming"
          return (
            <div key={idx} className="flex-1 relative">
              <div
                className={cx(
                  "h-[3px] rounded-full overflow-hidden transition-colors duration-300",
                  phase === "upcoming" ? "bg-zinc-100" : "bg-transparent",
                )}
              >
                {phase !== "upcoming" && (
                  <motion.div
                    initial={false}
                    animate={{ width: "100%" }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                    className={cx(
                      "h-full rounded-full",
                      phase === "done"
                        ? "bg-emerald-500"
                        : "bg-gradient-to-r from-emerald-500 to-emerald-400",
                    )}
                  />
                )}
              </div>
              {phase === "current" && (
                <motion.div
                  className="absolute -right-0.5 top-1/2 -translate-y-1/2 size-2 rounded-full bg-emerald-500 ring-2 ring-white"
                  animate={{ scale: [1, 1.18, 1] }}
                  transition={{
                    duration: 1.6,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  aria-hidden
                />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
